interface ErrorDetails {
  message: string;
  status?: number;
  body?: any;
  stack?: string;
}

interface RetryOptions {
  maxAttempts?: number;
  delay?: number;
}

export class AppError extends Error {
  public status?: number;
  public body?: any;
  
  constructor(message: string, status?: number, body?: any) {
    super(message);
    this.name = 'AppError';
    this.status = status;
    this.body = body;
  }
}

export const errorHandler = {
  // Log error details for debugging
  logError: (error: any, context: string) => {
    const errorDetails: ErrorDetails = {
      message: error.message || 'Unknown error',
      status: error.status || error.code,
      body: error.body || error.details,
      stack: error.stack
    };
    
    console.error(`[${context}] Error:`, errorDetails);
    
    // In production, you might want to send this to a logging service
    if (process.env.NODE_ENV === 'production') {
      // Send to logging service
    }
  },

  // Get user-friendly error message
  getUserMessage: (error: any): string => {
    if (error.message?.includes('network') || error.message?.includes('fetch')) {
      return 'Network connection issue. Please check your internet and try again.';
    }
    
    if (error.status === 401 || error.message?.includes('auth')) {
      return 'Authentication failed. Please sign in again.';
    }
    
    if (error.status === 403) {
      return 'Access denied. You may not have permission for this action.';
    }
    
    if (error.status === 404) {
      return 'The requested data was not found.';
    }
    
    if (error.status === 500 || error.status >= 500) {
      return 'Server error. Our team has been notified. Please try again later.';
    }
    
    if (error.message?.includes('Supabase')) {
      return 'Database connection issue. Please try again in a moment.';
    }
    
    if (error.message?.includes('OpenRouter') || error.message?.includes('AI')) {
      return 'AI service temporarily unavailable. Please try again later.';
    }
    
    return error.message || 'Something went wrong. Please try again.';
  },

  // Retry logic for API calls
  withRetry: async <T>(
    operation: () => Promise<T>,
    options: RetryOptions = {}
  ): Promise<T> => {
    const { maxAttempts = 2, delay = 1000 } = options;
    let lastError: any;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        
        if (attempt === maxAttempts) {
          throw error;
        }
        
        // Don't retry on auth errors or client errors (4xx)
        if (error.status >= 400 && error.status < 500) {
          throw error;
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
    
    throw lastError;
  }
};

// Hook for error handling in components
export const useErrorHandler = () => {
  const handleError = (error: any, context: string) => {
    errorHandler.logError(error, context);
    return errorHandler.getUserMessage(error);
  };
  
  return { handleError };
};
